import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check, X, Inbox, AlertTriangle, FileText } from 'lucide-react';
import { API_URL } from '../utils/api';
import { useLive } from '../context/LiveContext';
import PushSubscribeButton from './PushSubscribeButton';

const kindMeta = {
  'invoice.overdue':        { Icon: AlertTriangle, color: 'var(--error)' },
  'invoice.paid':           { Icon: Check, color: 'var(--success)' },
  'incoming.new':           { Icon: Inbox, color: 'var(--info)' },
  'incoming.pending_review':{ Icon: Inbox, color: 'var(--warning)' },
  'client.new':             { Icon: FileText, color: 'var(--accent-copper)' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'most';
  if (diff < 3600) return `${Math.floor(diff / 60)} perc`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} óra`;
  return `${Math.floor(diff / 86400)} nap`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();
  const { live } = useLive();

  const fetchUnread = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/notifications/unread-count`, { withCredentials: true });
      setUnread(r.data?.count ?? 0);
    } catch { /* silent */ }
  }, []);


  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      // trigger a scan so new items appear before we fetch
      await axios.post(`${API_URL}/api/notifications/scan`, {}, { withCredentials: true }).catch(() => {});
      const r = await axios.get(`${API_URL}/api/notifications?limit=25`, { withCredentials: true });
      setItems(r.data?.items || []);
      await fetchUnread();
    } catch { setItems([]); }
    setLoading(false);
  }, [fetchUnread]);

  useEffect(() => {
    if (live) setUnread(live.unread_notifications ?? 0);
  }, [live]);


  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  useEffect(() => {
    function onClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
    }
  }, [open]);

  const handleClick = async (n) => {
    try {
      await axios.post(`${API_URL}/api/notifications/${n.id}/read`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setItems(prev => prev.map(i => i.id === n.id ? { ...i, read: true } : i));
    setUnread(u => Math.max(0, u - 1));
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  const markAll = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`, {}, { withCredentials: true });
      setItems(prev => prev.map(i => ({ ...i, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    try { await axios.delete(`${API_URL}/api/notifications/${id}`, { withCredentials: true }); } catch { /* */ }
    setItems(prev => prev.filter(i => i.id !== id));
  };

  return (
    <div ref={boxRef} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="notification-bell"
        className="relative p-2 rounded-lg transition-colors"
        style={{ color: 'var(--text-secondary)' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--background-base)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        aria-label="Értesítések"
      >
        <Bell size={18} strokeWidth={1.75} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center"
            style={{ backgroundColor: 'var(--accent-copper)', color: 'var(--accent-copper-fg)', fontFamily: 'JetBrains Mono, monospace' }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
        {/* iter 251b — admin error badge (piros pötty) — CSAK vizuális jelző,
            NEM kattintható. Régen ez a badge navigált /admin-ra, ami elvitte
            a felhasználót az admin panelre, amikor csak az értesítés dropdown-t
            akarta megnyitni. Az admin gyors-link most a dropdown fejlécében él. */}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-[calc(100%+8px)] w-[380px] max-w-[95vw] rounded-xl overflow-hidden animate-zoom-fade origin-top-right"
          style={{ backgroundColor: 'var(--background-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-5)', zIndex: 60 }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-2">
              <Bell size={15} style={{ color: 'var(--text-secondary)' }} />
              <span className="font-heading font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Értesítések</span>
              {unread > 0 && <span className="text-xs font-medium" style={{ color: 'var(--accent-copper)' }}>{unread} új</span>}
            </div>
            {items.some(i => !i.read) && (
              <button
                onClick={markAll}
                data-testid="notification-mark-all"
                className="text-xs font-medium hover:underline"
                style={{ color: 'var(--text-secondary)' }}
              >
                Mind olvasott
              </button>
            )}
          </div>


          {/* Push subscribe toggle row */}
          <div className="px-4 py-2 border-b flex items-center justify-between text-xs" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background-base)' }}>
            <span style={{ color: 'var(--text-muted)' }}>Kapj natív push értesítést</span>
            <PushSubscribeButton />
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Betöltés…</div>
            ) : items.length === 0 ? (
              <div className="p-10 text-center">
                <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: 'var(--background-base)' }}>
                  <Bell size={20} style={{ color: 'var(--text-muted)' }} />
                </div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Nincs új értesítésed</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Minden naprakész.</p>
              </div>
            ) : (
              <ul>
                {items.map(n => {
                  const { Icon = Bell, color = 'var(--text-secondary)' } = kindMeta[n.kind] || {};
                  return (
                    <li key={n.id}>
                      <button
                        onClick={() => handleClick(n)}
                        data-testid={`notification-item-${n.id}`}
                        className="w-full group px-4 py-3 flex items-start gap-3 text-left transition-colors relative"
                        style={{ backgroundColor: n.read ? 'transparent' : 'var(--background-base)' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--background-base)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = n.read ? 'transparent' : 'var(--background-base)'}
                      >
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
                          <Icon size={16} style={{ color }} strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{n.title}</p>
                          {n.body && <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{n.body}</p>}
                          <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>{timeAgo(n.created_at)}</p>
                        </div>
                        {!n.read && <span className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: 'var(--accent-copper)' }} />}
                        <button
                          onClick={(e) => remove(e, n.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-black/10"
                          style={{ color: 'var(--text-muted)' }}
                          aria-label="Törlés"
                        >
                          <X size={13} />
                        </button>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
